import React, { useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger,
  DropdownMenuSubContent, DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import {
  MoreVertical, Trash2, Archive, RotateCcw, Ban, GitFork, AlertOctagon, Tag, Loader2, ShieldAlert, Beaker,
} from "lucide-react";

const CLASSIFICATIONS = ["Production", "Test Data", "Training / Demonstration", "Imported Legacy Data"];
const ACTION_LABELS = {
  delete: "Delete Permanently", archive: "Archive", restore: "Restore",
  void: "Void", supersede: "Supersede", entered_in_error: "Mark Entered in Error", reclassify: "Set Classification",
};
const LIFECYCLE_STATES = { void: "voided", supersede: "superseded", entered_in_error: "entered_in_error" };
const badge = "inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border";

export function RecordBadges({ classification, lifecycleState }) {
  const cls = classification || "Production";
  const lc = lifecycleState || "active";
  return (
    <>
      {cls === "Test Data" && <span className={`${badge} bg-amber-50 text-amber-800 border-amber-200`} data-testid="badge-test"><Beaker className="h-3 w-3" /> Test</span>}
      {cls === "Training / Demonstration" && <span className={`${badge} bg-purple-50 text-purple-800 border-purple-200`}><Beaker className="h-3 w-3" /> Training</span>}
      {cls === "Imported Legacy Data" && <span className={`${badge} bg-slate-100 text-slate-600 border-slate-200`}>Legacy</span>}
      {lc === "archived" && <span className={`${badge} bg-slate-100 text-slate-600 border-slate-200`}>Archived</span>}
      {lc === "voided" && <span className={`${badge} bg-red-50 text-red-700 border-red-200`}>Voided</span>}
      {lc === "superseded" && <span className={`${badge} bg-orange-50 text-orange-700 border-orange-200`}>Superseded</span>}
      {lc === "entered_in_error" && <span className={`${badge} bg-red-50 text-red-700 border-red-200`}>Entered in Error</span>}
    </>
  );
}

export function RecordActionsMenu({ type, id, onChanged, className = "" }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // {action}

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post("/removal/preview", { type, id });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load record actions."); }
    finally { setLoading(false); }
  }, [type, id]);

  const quickReclassify = async (classification) => {
    try {
      await api.post("/removal/reclassify", { type, id, classification, reason: `Set to ${classification}` });
      toast.success(`Classification set to ${classification}.`);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const doRestore = async () => {
    try { await api.post("/removal/restore", { type, id }); toast.success("Record restored."); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };

  const acts = preview?.allowed_actions || [];

  return (
    <>
      <DropdownMenu onOpenChange={(o) => { if (o) loadPreview(); }}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${className}`} data-testid={`record-actions-${type}`} onClick={(e) => e.stopPropagation()}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel className="text-[11px] text-muted-foreground">Record actions</DropdownMenuLabel>
          {loading && <div className="px-2 py-3 text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>}
          {!loading && preview && (
            <>
              {acts.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">No actions available for your role.</div>}
              {acts.includes("delete") && <DropdownMenuItem className="text-red-600 focus:text-red-700" onSelect={() => setDialog({ action: "delete" })} data-testid="action-delete"><Trash2 className="h-4 w-4 mr-2" /> Delete Permanently</DropdownMenuItem>}
              {acts.includes("archive") && <DropdownMenuItem onSelect={() => setDialog({ action: "archive" })} data-testid="action-archive"><Archive className="h-4 w-4 mr-2" /> Archive</DropdownMenuItem>}
              {acts.includes("restore") && <DropdownMenuItem onSelect={doRestore} data-testid="action-restore"><RotateCcw className="h-4 w-4 mr-2" /> Restore</DropdownMenuItem>}
              {acts.includes("void") && <DropdownMenuItem onSelect={() => setDialog({ action: "void" })} data-testid="action-void"><Ban className="h-4 w-4 mr-2" /> Void</DropdownMenuItem>}
              {acts.includes("supersede") && <DropdownMenuItem onSelect={() => setDialog({ action: "supersede" })} data-testid="action-supersede"><GitFork className="h-4 w-4 mr-2" /> Supersede</DropdownMenuItem>}
              {acts.includes("entered_in_error") && <DropdownMenuItem onSelect={() => setDialog({ action: "entered_in_error" })} data-testid="action-eie"><AlertOctagon className="h-4 w-4 mr-2" /> Mark Entered in Error</DropdownMenuItem>}
              {acts.includes("reclassify") && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="action-reclassify"><Tag className="h-4 w-4 mr-2" /> Set Classification</DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      {CLASSIFICATIONS.map((c) => (
                        <DropdownMenuItem key={c} onSelect={() => quickReclassify(c)} data-testid={`reclassify-${c.replace(/\W+/g, "-")}`}>
                          {c === preview.classification ? "● " : ""}{c}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-[10px] text-muted-foreground">
                <RecordBadges classification={preview.classification} lifecycleState={preview.lifecycle_state} />
                {preview.protected && <span className="flex items-center gap-1 text-amber-700 mt-1"><ShieldAlert className="h-3 w-3" /> Protected record</span>}
              </div>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog && preview && (
        <RemovalDialog type={type} id={id} action={dialog.action} preview={preview}
          onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged && onChanged(); }} />
      )}
    </>
  );
}

const REASONS = ["Test Data", "Duplicate", "Entered in Error", "Failed Import", "Abandoned Draft", "Customer Record Created in Error", "Other"];

function Row({ k, v }) { return (<div className="flex justify-between gap-4 py-1 text-xs border-b border-border/60 last:border-0"><span className="text-muted-foreground">{k}</span><span className="text-navy font-medium text-right">{v}</span></div>); }

function RemovalDialog({ type, id, action, preview, onClose, onDone }) {
  const isDelete = action === "delete";
  const isLifecycle = ["void", "supersede", "entered_in_error"].includes(action);
  const [reason, setReason] = useState(isDelete ? "" : (action === "archive" ? "" : ""));
  const [explanation, setExplanation] = useState("");
  const [cascade, setCascade] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (isDelete) {
        await api.post("/removal/delete", { type, id, reason, explanation, cascade });
        toast.success("Record permanently deleted.");
      } else if (action === "archive") {
        await api.post("/removal/archive", { type, id });
        toast.success("Record archived.");
      } else if (isLifecycle) {
        await api.post("/removal/lifecycle", { type, id, state: LIFECYCLE_STATES[action], reason, justification: explanation });
        toast.success(`Record marked ${ACTION_LABELS[action]}.`);
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed."); }
    finally { setBusy(false); }
  };

  const deps = preview.dependencies || [];
  const hasCascade = (preview.cascade_deletable || 0) > 0;
  const deleteReady = isDelete ? (reason && (reason !== "Other" || explanation.trim()) && confirmText === "DELETE") : true;
  const lifecycleReady = isLifecycle ? reason.trim().length > 0 : true;
  const canSubmit = deleteReady && lifecycleReady && !busy;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="removal-dialog">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isDelete ? "text-red-700" : "text-navy"}`}>
            {isDelete ? <Trash2 className="h-5 w-5" /> : isLifecycle ? <AlertOctagon className="h-5 w-5" /> : <Archive className="h-5 w-5" />}
            {ACTION_LABELS[action]} — {preview.label}
          </DialogTitle>
          <DialogDescription>
            {isDelete ? "This permanently removes the record. This action cannot be undone." :
              action === "archive" ? "Archived records leave active views but are preserved and can be restored." :
              "The record is preserved with full audit history and removed from active views. A reason is required."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded border border-border bg-slate-50/60 px-3 py-2">
            <Row k="Record type" v={preview.label} />
            <Row k="Name / ID" v={preview.name || preview.id} />
            {preview.customer && <Row k="Customer" v={preview.customer} />}
            <Row k="Current status" v={preview.status || "—"} />
            <Row k="Created by" v={preview.created_by || "—"} />
            <Row k="Created" v={preview.created_at ? String(preview.created_at).slice(0, 10) : "—"} />
            <Row k="Classification" v={preview.classification} />
            {isDelete && <Row k="Permanent" v="Yes — cannot be undone" />}
            {isDelete && <Row k="Attachments removed" v={preview.attachments_removed ? "Yes" : "No"} />}
          </div>

          {deps.length > 0 && (
            <div className="rounded border border-border" data-testid="removal-deps">
              <div className="px-3 py-1.5 text-[11px] font-semibold text-navy bg-slate-50 border-b">Related records ({preview.dependency_total})</div>
              {deps.map((d) => (
                <div key={d.type} className="flex justify-between px-3 py-1.5 text-xs border-b border-border/50 last:border-0">
                  <span className="text-muted-foreground">{d.label}</span>
                  <span className="text-navy">{d.count}{d.protected ? <span className="text-amber-700"> · {d.protected} protected</span> : ""}</span>
                </div>
              ))}
            </div>
          )}

          {preview.protected && isDelete && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex gap-2" data-testid="removal-protected">
              <ShieldAlert className="h-4 w-4 shrink-0" /> <span>Protected — {(preview.protection_reasons || []).join(" ")} Use Archive, Void, or Entered in Error instead.</span>
            </div>
          )}

          {hasCascade && isDelete && (
            <label className="flex items-start gap-2 text-xs rounded border border-border px-3 py-2">
              <Checkbox checked={cascade} onCheckedChange={(v) => setCascade(!!v)} data-testid="removal-cascade" />
              <span>Also permanently delete <b>{preview.cascade_deletable}</b> eligible draft/child dependencies. Protected dependencies are never deleted.</span>
            </label>
          )}

          {(isDelete || isLifecycle) && (
            <div className="space-y-1.5">
              <Label className="text-xs">{isDelete ? "Deletion reason" : "Reason"} <span className="text-red-500">*</span></Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="removal-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {((isDelete && reason === "Other") || isLifecycle) && (
            <div className="space-y-1.5">
              <Label className="text-xs">{isLifecycle ? "Justification" : "Written explanation"} {isDelete && reason === "Other" && <span className="text-red-500">*</span>}</Label>
              <Textarea rows={2} value={explanation} onChange={(e) => setExplanation(e.target.value)} data-testid="removal-explanation" />
            </div>
          )}

          {isDelete && !preview.protected && (
            <div className="space-y-1.5">
              <Label className="text-xs">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="removal-confirm-text" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {isDelete ? (
            <Button className="bg-red-600 text-white hover:bg-red-700" disabled={!canSubmit || preview.protected} onClick={submit} data-testid="removal-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />} Delete Permanently
            </Button>
          ) : (
            <Button className="bg-navy text-white hover:bg-navy-700" disabled={!canSubmit} onClick={submit} data-testid="removal-confirm">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Confirm {ACTION_LABELS[action]}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
